// Resolve the current user's tenant org id from the Supabase JWT.
//
// The auth-hook Edge Function (supabase/functions/auth-hook/index.ts) writes
// `org_id` + `primary_role` claims onto every issued JWT. The SPA already
// pulls `primary_role` via user_metadata in `lib/auth.tsx`; org_id lives in
// the JWT body itself, so we decode it here.
//
// Returns `null` until the session resolves so callers can no-op safely.

import { useMemo } from 'react'
import { useAuth } from '../../lib/auth'

export function useOrgId(): string | null {
  const { session } = useAuth()
  const accessToken = session?.access_token ?? null

  return useMemo(() => {
    if (!accessToken) return null
    return extractOrgIdFromJwt(accessToken)
  }, [accessToken])
}

/**
 * Decode a JWT body without verifying — Supabase already verified it on the
 * way in. We only want one claim. Returns `null` for any parse error or
 * missing claim so callers can fall back gracefully.
 *
 * Exported for tests.
 */
export function extractOrgIdFromJwt(jwt: string): string | null {
  try {
    const parts = jwt.split('.')
    if (parts.length < 2) return null
    const payload = parts[1]
    if (!payload) return null
    const padded = payload.padEnd(payload.length + ((4 - (payload.length % 4)) % 4), '=')
    const bin = atob(padded.replace(/-/g, '+').replace(/_/g, '/'))
    const json = JSON.parse(bin) as Record<string, unknown>
    const orgId = json['org_id']
    return typeof orgId === 'string' && orgId.length > 0 ? orgId : null
  } catch {
    return null
  }
}
